import { Lender } from '@1delta/lender-registry'
import { Call } from '../../../utils/multicall'
import {
  getCompoundV2Comptroller,
  getCompoundV2Lens,
  getCompoundV2Tokens,
  VENUS_LENS_VBNB,
} from '../addresses/compoundV2'
import { Chain } from '@1delta/chain-registry'
import { VenusLensLegacyAbi } from '../../../abis/compound-v2/VenusLensLegacy'
import {
  isBenqiType,
  isKineticType,
  isSumerType,
  isTectonicType,
  isVenusType,
} from '../../../utils'
import {
  SumerLensAbi,
  SumerComptrollerAbi,
} from '../../../abis/compound-v2/SumerLens'
import { SUMER_GROUP_COUNT } from './getters/sumer'
import { TakaraMarketStateAbi } from '../../../abis/compound-v2/TakaraMarketState'
import { TectonicLensAbi } from '../../../abis/compound-v2/TectonicLens'
import { KineticLensAbi } from '../../../abis/compound-v2/KineticLens'
import {
  BenqiLensAbi,
  BenqiEcoLensAbi,
} from '../../../abis/compound-v2/BenqiLens'
import { CompoundLensAbi } from '../../../abis/compound-v2/CompoundLens'
import {
  CreamLensAbi,
  CreamLensV2Abi,
} from '../../../abis/compound-v2/CreamLens'
import { SegmentLensAbi } from '../../../abis/compound-v2/SegmentLens'
import { ComptrollerAbi } from '../../../abis/compound-v2/Comptroller'
import { usesComptrollerPauseReads } from './pause'

export const buildCompoundV2StyleLenderReserveCall = (
  chainId: string,
  lender: string,
) => {
  /**
   * EVERY path below reads through a lens, so without one on this chain the
   * per-market calls are built with `address: undefined` and dispatched anyway
   * — the batch either throws or comes back garbage, for the whole lender.
   *
   * That was live for nine (lender, chain) pairs whose fork is on
   * `ENABLED_COMPOUNDS` but whose lens map does not cover the chain (Venus on
   * zkSync, Moonwell on Moonbeam, Segment on opBNB/BOB, Sumer on Meter/Bitlayer/
   * zkLink, Capy on World Chain, …). `lender-metadata` adding a chain to a fork
   * that is already enabled is enough to create one, which is exactly how the
   * 2026-08 sweep produced five more.
   *
   * Returning no calls makes the parser's length check fail (it expects
   * tokenCount + tail), so the lender is dropped for this chain — the documented
   * "missing venue, never corrupt data" outcome — and nothing is sent to the RPC.
   */
  if (!getCompoundV2Lens(chainId, lender)) return []

  // TAKARA: single call fetches all markets at once
  if (lender === Lender.TAKARA) {
    const lens = getCompoundV2Lens(chainId, lender)
    return [
      {
        abi: TakaraMarketStateAbi,
        address: lens,
        name: 'getActiveMarketsInfo',
        params: [],
      },
    ]
  }

  // TECTONIC: single call fetches all markets at once via comptroller
  if (isTectonicType(lender)) {
    const lens = getCompoundV2Lens(chainId, lender)
    const comptroller = getCompoundV2Comptroller(chainId, lender)
    return [
      {
        abi: TectonicLensAbi,
        address: lens,
        name: 'getMarketsData',
        params: [comptroller],
      },
    ]
  }

  // KINETIC: each isolated pool has its own Lens (comptroller-pinned), so a
  // single getMarketMetadataForAllMarkets() call returns every market for the
  // pool. We never pass a comptroller — the Lens already knows which one.
  if (isKineticType(lender)) {
    const lens = getCompoundV2Lens(chainId, lender)
    return [
      {
        abi: KineticLensAbi,
        address: lens,
        name: 'getMarketMetadataForAllMarkets',
        params: [],
      },
    ]
  }

  // BENQI: single call fetches all markets at once (comptroller baked into lens)
  if (isBenqiType(lender)) {
    const lens = getCompoundV2Lens(chainId, lender)
    const abi =
      lender === Lender.BENQI_AVALANCHE_ECOSYSTEM
        ? BenqiEcoLensAbi
        : BenqiLensAbi
    return [
      {
        abi,
        address: lens,
        name: 'getMarketMetadataForAllMarkets',
        params: [],
      },
    ]
  }

  const tokens = getCompoundV2Tokens(chainId, lender)

  const getCTokenCall = getCall(lender, chainId)
  const calls: Call[] = tokens.map((tk) => getCTokenCall(tk.cToken))

  // For Sumer: append 5 calls to fetch CompactAssetGroup for groups 0-4.
  // Sumer surfaces liquidation incentives per-market (homo/hetero) and does not
  // use the standard comptroller closeFactor/liquidationIncentive reads.
  if (isSumerType(lender)) {
    const comptroller = getCompoundV2Comptroller(chainId, lender)
    if (comptroller) {
      for (let groupId = 0; groupId < SUMER_GROUP_COUNT; groupId++) {
        // abi override picked up via call.abi in fetchLender.ts (@ts-ignore there)
        ;(calls as any[]).push({
          abi: SumerComptrollerAbi,
          address: comptroller,
          name: 'assetGroup',
          params: [groupId],
        })
      }
    }
    return calls
  }

  // Standard Compound V2 path: append global comptroller reads for
  // closeFactorMantissa() and liquidationIncentiveMantissa() when a comptroller
  // address is known for this fork. These two calls are always appended as the
  // last two entries so the parser can slice them off the tail. If no
  // comptroller is configured, the parser falls back to defaults.
  //
  // Layout (must stay in lockstep with publicCallParse.ts):
  //   [0 .. n-1]        cToken metadata, one per token
  //   [n]               closeFactorMantissa()
  //   [n+1]             liquidationIncentiveMantissa()
  //   [n+2 .. 2n+1]     mintGuardianPaused(cToken_i)     — pause-read forks
  //   [2n+2 .. 3n+1]    borrowGuardianPaused(cToken_i)   — pause-read forks
  const stdComptroller = getCompoundV2Comptroller(chainId, lender)
  if (stdComptroller) {
    // abi override picked up via call.abi in fetchLender.ts
    ;(calls as any[]).push({
      abi: ComptrollerAbi,
      address: stdComptroller,
      name: 'closeFactorMantissa',
      params: [],
    })
    ;(calls as any[]).push({
      abi: ComptrollerAbi,
      address: stdComptroller,
      name: 'liquidationIncentiveMantissa',
      params: [],
    })

    // Per-market pause guardians for the forks whose lens omits them. Two
    // grouped blocks (all mints, then all borrows) rather than interleaved, so
    // the parser slices with plain offsets.
    if (usesComptrollerPauseReads(lender)) {
      for (const name of ['mintGuardianPaused', 'borrowGuardianPaused']) {
        for (const tk of tokens) {
          ;(calls as any[]).push({
            abi: ComptrollerAbi,
            address: stdComptroller,
            name,
            params: [tk.cToken],
          })
        }
      }
    }
  }

  return calls
}

function getCall(lender: string, chainId: string) {
  const lens = getCompoundV2Lens(chainId, lender)
  if (lender === Lender.SEGMENT) {
    return (tk: string) => ({
      abi: SegmentLensAbi,
      address: lens,
      name: 'seTokenMetadata',
      params: [tk],
    })
  }
  if (isVenusType(lender)) {
    return (tk: string) =>
      chainId === Chain.BNB_SMART_CHAIN_MAINNET &&
      tk === '0xa07c5b74c9b40447a954e1466938b865b6bbea36' // VBNB
        ? {
            abi: VenusLensLegacyAbi,
            address: VENUS_LENS_VBNB,
            name: 'vTokenMetadata',
            params: [tk],
          }
        : {
            address: lens,
            name: 'vTokenMetadata',
            params: [tk],
          }
  }
  if (lender === Lender.MOONWELL) {
    return (tk: string) => ({
      address: lens,
      name: 'getMarketInfo',
      params: [tk],
    })
  }
  // Sumer: use per-call ABI override
  if (isSumerType(lender)) {
    return (tk: string) => ({
      abi: SumerLensAbi,
      address: lens,
      name: 'cTokenMetadata',
      params: [tk],
    })
  }
  // Cream on Polygon/Arbitrum: 17-field variant (with totalCollateralTokens, version, collateralCap)
  if (
    lender === Lender.CREAM_FINANCE &&
    (chainId === Chain.POLYGON_MAINNET || chainId === Chain.ARBITRUM_ONE)
  ) {
    return (tk: string) => ({
      abi: CreamLensV2Abi,
      address: lens,
      name: 'cTokenMetadata',
      params: [tk],
    })
  }
  // 14-field CTokenMetadata (no comp speeds / borrow cap)
  if (
    lender === Lender.CREAM_FINANCE ||
    lender === Lender.GAMMA ||
    lender === Lender.TENDER ||
    lender === Lender.COMPOUND_V2 ||
    lender === Lender.CAPY_FI ||
    lender === Lender.FLUX_FINANCE ||
    lender === Lender.WE_PIGGY ||
    // long-tail forks served by a shared lens on their chain — see
    // SHARED_LENS_BNB / SHARED_LENS_ARBITRUM in addresses/compoundV2.ts
    lender === Lender.DEMETER ||
    lender === Lender.LANDER ||
    lender === Lender.FILDA ||
    lender === Lender.SOLIDLIZARD ||
    // served by our own CompoundV2MarketLens deployment (same 14-field struct)
    lender === Lender.IRON_BANK ||
    lender === Lender.MENDI ||
    lender === Lender.PAXO ||
    lender === Lender.SONNE ||
    lender === Lender.ASO_FINANCE ||
    lender === Lender.BLUME ||
    lender === Lender.KAWA ||
    lender === Lender.XPERT ||
    lender === Lender.KEOM ||
    lender === Lender.REACTOR_FUSION
  ) {
    return (tk: string) => ({
      abi: CreamLensAbi,
      address: lens,
      name: 'cTokenMetadata',
      params: [tk],
    })
  }
  // Lodestar: 17-field CTokenMetadata (with comp speeds + borrow cap)
  if (lender === Lender.LODESTAR) {
    return (tk: string) => ({
      abi: CompoundLensAbi,
      address: lens,
      name: 'cTokenMetadata',
      params: [tk],
    })
  }
  // default
  return (tk: string) => ({
    address: lens,
    name: 'cTokenMetadata',
    params: [tk],
  })
}
